import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * What a page shows when it fails while drawing (2026-10-14).
 *
 * The router's default error component, so a page with no error handling of its own is replaced by
 * this, inside the shell, rather than taking the whole console down with it (`shell-failure.tsx` is
 * the shell's own, for the sign-in read). It says what failed, with the request id when wetalk itself
 * failed, and offers Try again, which draws the page again and re-runs its reads.
 *
 * A chunk of a build that a deploy has since replaced (`../stale-build.ts`) reloads the page once
 * to the current build instead; a second failure soon after is shown here like any other.
 */
import { useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { Button } from "@wetalk/ui";
import { useEffect, type ReactNode } from "react";

import { apiProblemText } from "../api/api-error.js";
import { browserStaleBuildReload, isStaleBuildError, reloadOnceForStaleBuild } from "../stale-build.js";

export function PageFailure({ error, reset }: ErrorComponentProps): ReactNode {
  usePortalTranslation("shell");
  const router = useRouter();
  const staleBuild = isStaleBuildError(error);
  useEffect(() => {
    if (staleBuild) reloadOnceForStaleBuild(browserStaleBuildReload());
  }, [staleBuild]);
  return (
    <section className="c-page" role="alert">
      <h1>{portalText("shell", "this_page_could_not_load_f25d91d4")}</h1>
      <p className="c-note c-note-critical">{apiProblemText(error, portalText("shell", "something_on_it_failed_e9ecbbc2"))}</p>
      <Button
        variant="primary"
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        {portalText("shell", "try_again_042c862e")}
      </Button>
    </section>
  );
}
